import React from 'react';
import { Button } from '@parsina/framework';
import { Clock } from 'lucide-react';
import { C, GRADIENT_BRAND } from '../constants/theme';
import { useTranslation } from '../i18n/I18nProvider.jsx';
import { displayName } from '../lib/appointments.js';
import StatusPill from './StatusPill.jsx';
import NextServiceChip from './NextServiceChip.jsx';
import PatientAvatar from './PatientAvatar.jsx';

// The «بیمار بعدی» hero — desktop.
//
// The single most important fact of the day sheet is who walks in next, and an
// info Alert gave it the visual weight of a footnote. This is the desktop
// counterpart of `mobile/MobileNextPatient.jsx`: same brand gradient, same
// vocabulary (StatusPill, displayName, today.next.*), laid out as one wide row
// because the reception monitor has the width the phone lacks.
//
// `apt` is `buildDayModel().nextApt` — null off today, null with nobody
// waiting. This component never decides who is next. Nor does it decide what
// they are coming FOR: `service` is read beside it (hooks/useNextService.js)
// and null renders as «ویزیت».

const NextPatientHero = ({ apt, service, onOpen, onOpenVisit }) => {
  const { t, formatDigits } = useTranslation();
  if (!apt) return null;

  const name = displayName(apt) || t('today.row.unknownPatient');
  // A «ثبت سریع» walk-in has no patient record, so there is nothing to open:
  // both actions disappear rather than navigate nowhere (§3.2).
  const hasRecord = Boolean(apt.patientId);

  return (
    <section
      data-testid="today-next"
      aria-label={t('today.next.title')}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: GRADIENT_BRAND,
        borderRadius: 16,
        padding: '20px 24px',
        marginBottom: 18,
        color: '#fff',
        boxShadow: '0 8px 24px color-mix(in srgb, var(--p-navy) 28%, transparent)',
      }}
    >
      {/* Decorative depth only — aria-hidden, white-on-white, no information. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          insetInlineEnd: -70,
          top: -70,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          insetInlineEnd: 90,
          bottom: -90,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }}
      />

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 18 }}>
        <PatientAvatar name={name} src={apt.patient?.avatarUrl} size={56} bg="rgba(255,255,255,0.22)" />

        <div style={{ minWidth: 0, flex: 1 }}>
          {/* h2, matching the day-sheet blocks: the hero must appear in a
              screen reader's heading list alongside «اتاق انتظار». */}
          <h2
            style={{
              margin: '0 0 4px',
              fontSize: 12,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.78)',
            }}
          >
            {t('today.next.title')}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div
              dir="auto"
              style={{
                fontSize: 22,
                fontWeight: 800,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </div>
            <StatusPill status={apt.status} />
          </div>
          {apt.time && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 4,
                fontSize: 13,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              <Clock size={14} strokeWidth={2.4} aria-hidden style={{ flexShrink: 0 }} />
              {t('today.next.at', { time: formatDigits(apt.time) })}
            </div>
          )}

          {/* WHAT they are here for, under WHEN. A walk-in with no record has
              no plan to read and no ویزیت to promise either — the hero says
              nothing rather than guessing. */}
          {hasRecord && <NextServiceChip service={service} />}
        </div>

        {hasRecord && (
          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            {/* جلسه is the primary action on the day sheet's rows, so it is the
                solid one here too — white on the gradient, navy text. */}
            <Button
              onClick={() => onOpenVisit?.(apt)}
              data-testid="today-next-visit"
              aria-label={t('today.row.visitFor', { name })}
              className="hover:bg-white/90"
              style={{ background: '#fff', color: C.navy, fontWeight: 800 }}
            >
              {t('today.row.visit')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => onOpen?.(apt)}
              data-testid="today-next-open"
              aria-label={t('today.row.openFor', { name })}
              className="text-white hover:bg-white/25"
              style={{ background: 'rgba(255,255,255,0.16)', color: '#fff' }}
            >
              {t('today.row.open')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default NextPatientHero;
